import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLang } from '../contexts/LanguageContext';
import { t } from '../utils/translations';
import api from '../utils/api';
import Layout from '../components/Layout';
import AssignButton from '../components/AssignButton';
import { toast } from 'sonner';
import { StatusBadge, CategoryBadge, HashtagChip, VisibilityBadge } from '../components/Badges';
import { Plus, Search, RotateCcw, Trash2, ChevronRight, AlertTriangle, Clock, Send, Inbox, Eye } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';

const VIEWS = [
  { key: 'created', icon: Send, color: '#F77F00', labelFr: 'Émis par moi', labelEn: 'Created by me', shortFr: 'Émis', shortEn: 'Mine', descFr: 'Tickets créés', descEn: 'Tickets I created' },
  { key: 'assigned', icon: Inbox, color: '#EF4444', okColor: '#22C55E', labelFr: 'À traiter', labelEn: 'To handle', shortFr: 'À traiter', shortEn: 'Handle', descFr: 'Assignés à moi', descEn: 'Assigned to me' },
  { key: 'scope', icon: Eye, color: '#3B82F6', labelFr: 'Dans mon champ', labelEn: 'In my scope', shortFr: 'Tout voir', shortEn: 'All', descFr: 'Visibles pour moi', descEn: 'Visible to me' },
];

const STATUS_FILTERS = [
  { key: '', labelFr: 'Tous', labelEn: 'All' },
  { key: 'open', labelFr: 'Ouverts', labelEn: 'Open' },
  { key: 'in_progress', labelFr: 'En cours', labelEn: 'In progress' },
  { key: 'resolved', labelFr: 'Résolus', labelEn: 'Resolved' },
  { key: 'closed', labelFr: 'Fermés', labelEn: 'Closed' },
];

export default function TicketListPage({ allTickets = false }) {
  const { user } = useAuth();
  const { lang } = useLang();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [tickets, setTickets] = useState([]);
  const [counters, setCounters] = useState({ created: 0, to_handle: 0, in_scope: 0 });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterVisibility, setFilterVisibility] = useState('');
  const [categories, setCategories] = useState([]);
  // View tab — allTickets page defaults to 'scope', Mes Tickets defaults to 'created'
  const [view, setView] = useState(allTickets ? 'scope' : 'created');

  const fetchData = useCallback(async () => {
    try {
      const params = { view };
      if (filterStatus) params.status = filterStatus;
      if (filterCategory) params.category = filterCategory;
      if (filterVisibility) params.visibility_filter = filterVisibility;
      const hashtag = searchParams.get('hashtag');
      if (hashtag) params.hashtag = hashtag;
      const [ticketsRes, countersRes] = await Promise.all([
        api.get('/tickets', { params }),
        api.get('/tickets/counters'),
      ]);
      setTickets(ticketsRes.data);
      setCounters(countersRes.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [view, filterStatus, filterCategory, filterVisibility, searchParams]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { api.get('/categories').then(res => setCategories(res.data)).catch(() => {}); }, []);

  // Reset view when switching between Mes Tickets and Tous les Tickets
  useEffect(() => { setView(allTickets ? 'scope' : 'created'); }, [allTickets]);

  const handleDelete = async (e, tid) => {
    e.stopPropagation();
    if (!window.confirm(lang === 'fr' ? 'Supprimer ce ticket ?' : 'Delete this ticket?')) return;
    try { await api.delete('/tickets/' + tid); fetchData(); } catch {}
  };

  const handleReset = async (e, ticket) => {
    e.stopPropagation();
    try { await api.put('/tickets/' + ticket._id, { status: 'open' }); fetchData(); } catch {}
  };

  const canManage = user && ['consultant', 'dr', 'dn', 'direction', 'support'].includes(user.role);

  // Filter by search (client-side)
  const filtered = tickets.filter(ticket => {
    if (!searchTerm) return true;
    const s = searchTerm.toLowerCase();
    const title = lang === 'en' ? (ticket.title_en || ticket.title_fr) : (ticket.title_fr || ticket.title_en);
    return title.toLowerCase().includes(s) || ticket.hashtags?.some(h => h.toLowerCase().includes(s));
  });

  function counterFor(k) {
    if (k === 'created') return counters.created;
    if (k === 'assigned') return counters.to_handle;
    if (k === 'scope') return counters.in_scope;
    return 0;
  }

  return (
    <Layout>
      <div className="animate-fade-in">
        <div className="flex flex-wrap items-start justify-between gap-3 mb-6">
          <div className="min-w-0 flex-1">
            <h1 data-testid="page-title" className="text-xl sm:text-2xl font-bold text-white">
              {allTickets ? t('all_tickets', lang) : t('my_tickets', lang)}
            </h1>
            <p className="text-xs sm:text-sm mt-1 truncate" style={{ color: '#9CA3AF' }}>{t('scope_' + (user?.role || 'member'), lang)}</p>
          </div>
          {user?.role !== 'support' && (
            <button data-testid="create-ticket-button" onClick={() => navigate('/tickets/new')} className="apollo-btn-primary flex items-center gap-2 flex-shrink-0" style={{ minHeight: '44px' }}>
              <Plus size={18} /> <span className="hidden sm:inline">{t('create_ticket', lang)}</span><span className="sm:hidden">{lang === 'fr' ? 'Créer' : 'New'}</span>
            </button>
          )}
        </div>

        {/* C5 - 3 KPI cards cliquables */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
          {VIEWS.map(v => {
            const count = counterFor(v.key);
            const isAssigned = v.key === 'assigned';
            const col = isAssigned ? (count > 0 ? v.color : v.okColor) : v.color;
            const active = view === v.key;
            const Icon = v.icon;
            return (
              <button
                key={v.key}
                data-testid={'kpi-card-' + v.key}
                onClick={() => setView(v.key)}
                className="apollo-card p-4 flex items-center justify-between text-left transition-all min-w-0"
                style={{ borderLeft: '3px solid ' + col, outline: active ? '1px solid ' + col : 'none' }}
              >
                <div className="min-w-0 flex-1">
                  <p className="text-[10px] sm:text-xs uppercase tracking-wider mb-1 truncate" style={{ color: '#9CA3AF' }}>
                    {lang === 'fr' ? v.labelFr : v.labelEn}
                  </p>
                  <p className="text-2xl sm:text-3xl font-bold text-white leading-none">{count}</p>
                  <p className="text-[10px] sm:text-xs mt-1 truncate" style={{ color: '#6B7280' }}>
                    {lang === 'fr' ? v.descFr : v.descEn}
                  </p>
                </div>
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: col + '20' }}>
                  <Icon size={20} style={{ color: col }} />
                </div>
              </button>
            );
          })}
        </div>

        {/* C5 - 3 onglets (labels courts < sm) */}
        <div data-testid="view-tabs" className="flex gap-2 mb-4 overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0 pb-1">
          {VIEWS.map(v => {
            const count = counterFor(v.key);
            const isAssigned = v.key === 'assigned';
            const col = isAssigned ? (count > 0 ? v.color : v.okColor) : v.color;
            const active = view === v.key;
            const Icon = v.icon;
            const label = lang === 'fr' ? v.labelFr : v.labelEn;
            const shortLabel = lang === 'fr' ? v.shortFr : v.shortEn;
            return (
              <button
                key={v.key}
                data-testid={'view-tab-' + v.key}
                onClick={() => setView(v.key)}
                className="px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-semibold transition-all flex items-center gap-2 whitespace-nowrap flex-shrink-0"
                style={{
                  background: active ? col : col + '12',
                  color: active ? '#fff' : col,
                  border: active ? 'none' : '1px solid ' + col + '40',
                  minHeight: '40px',
                }}
              >
                <Icon size={14} />
                <span className="sm:hidden">{shortLabel}</span>
                <span className="hidden sm:inline">{label}</span>
                <span>({count})</span>
              </button>
            );
          })}
        </div>

        {/* Filtres rapides statut - scroll-x sur mobile */}
        <div className="flex items-center gap-2 mb-3 overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0 sm:flex-wrap pb-1">
          {STATUS_FILTERS.map(f => {
            const active = filterStatus === f.key;
            return (
              <button
                key={f.key || 'all'}
                data-testid={'status-chip-' + (f.key || 'all')}
                onClick={() => setFilterStatus(f.key)}
                className="px-3 py-1.5 rounded-full text-xs font-semibold transition-all whitespace-nowrap flex-shrink-0"
                style={{
                  background: active ? '#F77F00' : 'transparent',
                  color: active ? '#fff' : '#9CA3AF',
                  border: '1px solid ' + (active ? '#F77F00' : 'rgba(255,255,255,0.12)'),
                  minHeight: '32px',
                }}
              >
                {lang === 'fr' ? f.labelFr : f.labelEn}
              </button>
            );
          })}
        </div>

        {/* Search + selects : pleine largeur mobile, inline desktop */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 mb-4">
          <div className="relative flex-1 sm:max-w-xs">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: '#6B7280' }} />
            <input data-testid="search-tickets" type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="apollo-input w-full" placeholder={t('search', lang)} style={{ minHeight: '44px', paddingLeft: '40px' }} />
          </div>
          <select data-testid="filter-category" value={filterCategory} onChange={e => setFilterCategory(e.target.value)} className="apollo-input w-full sm:w-auto" style={{ minHeight: '44px', background: '#1A1A2E', cursor: 'pointer' }}>
            <option value="">{t('category', lang)}</option>
            {categories.map(c => <option key={c.name} value={c.name}>{c.name}</option>)}
          </select>
          <select data-testid="filter-visibility" value={filterVisibility} onChange={e => setFilterVisibility(e.target.value)} className="apollo-input w-full sm:w-auto" style={{ minHeight: '44px', background: '#1A1A2E', cursor: 'pointer' }}>
            <option value="">{t('visibility', lang)}</option>
            <option value="private">{t('private', lang)}</option>
            <option value="pending_public">{t('pending_public', lang)}</option>
            <option value="public">{t('public', lang)}</option>
          </select>
        </div>

        {loading ? (
          <p className="text-center py-12" style={{ color: '#6B7280' }}>{t('loading', lang)}</p>
        ) : filtered.length === 0 ? (
          <div data-testid="no-tickets" className="text-center py-16"><p className="text-lg" style={{ color: '#6B7280' }}>{t('no_tickets', lang)}</p></div>
        ) : (
          <div className="space-y-2">
            {filtered.map((ticket, idx) => {
              const isCreator = ticket.created_by === user?._id;
              const canAssign = !ticket.assigned_to && (user?.role !== 'member' || isCreator);
              return (
              <div key={ticket._id + '-' + idx} data-testid={'ticket-row-' + ticket._id} onClick={() => navigate('/tickets/' + ticket._id)}
                className="apollo-card p-3 sm:p-4 flex flex-col sm:flex-row sm:items-center gap-3 cursor-pointer transition-all animate-fade-in" style={{ animationDelay: (idx * 30) + 'ms' }}>
                {/* Ligne 1 mobile + left desktop */}
                <div className="flex items-start gap-3 flex-1 min-w-0">
                  <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full flex-shrink-0 flex items-center justify-center" style={{ background: 'rgba(247,127,0,0.15)' }}>
                    <span style={{ color: '#F77F00', fontWeight: 600, fontSize: 13 }}>{ticket.created_by_name?.charAt(0) || 'U'}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="font-semibold text-white text-sm sm:text-base truncate max-w-full">{lang === 'en' ? (ticket.title_en || ticket.title_fr) : (ticket.title_fr || ticket.title_en)}</span>
                      <CategoryBadge category={lang === 'en' ? t(ticket.category, lang) : ticket.category} />
                      <VisibilityBadge visibility={ticket.visibility || 'private'} lang={lang} />
                      {!ticket.assigned_to && (
                        <span data-testid={'badge-unassigned-' + ticket._id} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium" style={{ background: 'rgba(239,68,68,0.12)', color: '#EF4444' }}>
                          <AlertTriangle size={11} /> {lang === 'fr' ? 'À assigner' : 'To assign'}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs truncate max-w-full" style={{ color: '#6B7280' }}>{t('created_by', lang)} {ticket.created_by_name} - {new Date(ticket.created_at).toLocaleDateString(lang === 'fr' ? 'fr-FR' : 'en-US')}</span>
                      {ticket.hashtags?.map(tag => <HashtagChip key={tag} tag={tag} onClick={(tg) => navigate('/tickets?hashtag=' + tg)} />)}
                    </div>
                  </div>
                </div>
                {/* Ligne 2 mobile = footer actions pleine largeur, desktop = right inline */}
                <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0 flex-wrap sm:flex-nowrap sm:justify-end justify-between pt-2 sm:pt-0 border-t sm:border-t-0 -mx-3 sm:mx-0 px-3 sm:px-0" style={{ borderColor: 'rgba(255,255,255,0.04)' }} onClick={e => e.stopPropagation()}>
                  <div className="flex items-center gap-2 flex-wrap">
                    {ticket.sla && ticket.sla.sla_breached && !ticket.sla.sla_responded && (ticket.status === 'open' || ticket.status === 'in_progress') && (
                      <span data-testid={'sla-breach-' + ticket._id} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold" style={{ background: 'rgba(239,68,68,0.15)', color: '#EF4444' }}>
                        <AlertTriangle size={12} /> SLA
                      </span>
                    )}
                    {ticket.sla && !ticket.sla.sla_breached && !ticket.sla.sla_responded && (ticket.status === 'open' || ticket.status === 'in_progress') && ticket.sla.sla_hours_remaining < 4 && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium" style={{ background: 'rgba(251,191,36,0.15)', color: '#FBBF24' }}>
                        <Clock size={12} /> {ticket.sla.sla_hours_remaining}h
                      </span>
                    )}
                    <StatusBadge status={ticket.status} />
                  </div>
                  <div className="flex items-center gap-1">
                    {canAssign && (
                      <AssignButton
                        ticketId={ticket._id}
                        size="sm"
                        onAssigned={(u) => {
                          toast.success(lang === 'fr' ? ('Ticket assigné à ' + (u?.name || '')) : ('Ticket assigned to ' + (u?.name || '')));
                          fetchData();
                        }}
                      />
                    )}
                    {canManage && (
                      <>
                        <button data-testid={'reset-ticket-' + ticket._id} onClick={(e) => handleReset(e, ticket)} className="rounded-lg hover:bg-white/5 flex items-center justify-center" style={{ color: '#F77F00', width: 36, height: 36 }} aria-label="Reset"><RotateCcw size={16} /></button>
                        <button data-testid={'delete-ticket-' + ticket._id} onClick={(e) => handleDelete(e, ticket._id)} className="rounded-lg hover:bg-white/5 flex items-center justify-center" style={{ color: '#EF4444', width: 36, height: 36 }} aria-label="Delete"><Trash2 size={16} /></button>
                      </>
                    )}
                    <ChevronRight size={16} className="hidden sm:block" style={{ color: '#6B7280' }} />
                  </div>
                </div>
              </div>
              );
            })}
          </div>
        )}
      </div>
    </Layout>
  );
}
